/**
 * Hooks para DJ 1930 — Endeudamiento con el exterior.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export type InstrumentType =
  | 'credito' | 'bono' | 'mutuo' | 'linea' | 'arriendo'
  | 'factoring' | 'cta_cte' | 'otro'

export type GuaranteeType = 'sin' | 'real' | 'personal' | 'estatal' | 'otra'

export interface ForeignDebt {
  id: string
  creditor_name: string
  creditor_tax_id: string
  creditor_country: string
  is_related_party: boolean
  instrument_type: InstrumentType
  instrument_type_display: string
  guarantee_type: GuaranteeType
  guarantee_type_display: string
  currency: string
  principal_amount: string
  outstanding_balance: string
  interest_rate: string
  disbursement_date: string
  maturity_date: string | null
  related_supplier: string | null
  related_supplier_name: string | null
  is_active: boolean
  notes: string
  created_at: string
  updated_at: string
}

export interface ForeignDebtPayload {
  creditor_name: string
  creditor_tax_id?: string
  creditor_country: string
  is_related_party?: boolean
  instrument_type: InstrumentType
  guarantee_type: GuaranteeType
  currency: string
  principal_amount: string
  outstanding_balance?: string
  interest_rate?: string
  disbursement_date: string
  maturity_date?: string | null
  related_supplier?: string | null
  is_active?: boolean
  notes?: string
}

interface PaginatedResponse<T> {
  results: T[]
  count: number
}

export function useForeignDebts(params: { is_active?: boolean } = {}) {
  return useQuery<PaginatedResponse<ForeignDebt>>({
    queryKey: ['foreign-debt', params],
    queryFn: async () => {
      const search: Record<string, string> = {}
      if (params.is_active !== undefined) search.is_active = String(params.is_active)
      const { data } = await apiClient.get('/treasury/foreign-debts/', { params: search })
      return data
    },
  })
}

export function useCreateForeignDebt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ForeignDebtPayload) =>
      (await apiClient.post<ForeignDebt>('/treasury/foreign-debts/', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['foreign-debt'] }),
  })
}

export function useUpdateForeignDebt(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<ForeignDebtPayload>) =>
      (await apiClient.patch<ForeignDebt>(`/treasury/foreign-debts/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['foreign-debt'] }),
  })
}

export function useDeleteForeignDebt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/treasury/foreign-debts/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['foreign-debt'] }),
  })
}

export interface DJ1930Item {
  creditor_name: string
  creditor_tax_id: string
  creditor_country: string
  is_related_party: string
  instrument_code: string
  guarantee_code: string
  currency: string
  principal_amount: string
  outstanding_balance: string
  interest_rate: string
  disbursement_date: string | null
  maturity_date: string | null
}

export interface DJ1930Candidate {
  supplier_id: string
  supplier_name: string
  supplier_tax_id: string
  supplier_country: string
  invoice_count: number
  total_billed: string
}

export interface DJ1930Preview {
  year: number
  items: DJ1930Item[]
  candidates: DJ1930Candidate[]
  totals: { count: number; candidates_count: number }
}
